import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';

import { PageCategory } from '~/features/analytics/constants';
import { useLeadFormEvent } from '~/features/analytics/hooks/use-lead-form-event';
import { DEFAULT_LANGUAGE } from '~/features/internationalization/languages';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';
import { getPageTemplate } from '~/features/page-template/controllers/get-page-template';
import { TemplateForm } from '~/features/page-template/models/template-form';
import {
  WhiteCardFormTemplate,
  whiteCardFormTemplateQuery,
  whiteCardFormTemplatesSchema,
} from '~/features/page-template/templates/white-card-form/model';
import { WhiteCardForm } from '~/features/page-template/templates/white-card-form/white-card-form';
import { transformFormDataToEventProperties } from '~/utils/transform-form-data-to-event-properties';

export default function PartnershipsContactPage({
  page,
  whiteCardFormTemplate,
}: {
  page: Page;
  whiteCardFormTemplate: WhiteCardFormTemplate;
}) {
  const router = useRouter();
  const { sendSegmentLeadFormEvents, isSubmitting } = useLeadFormEvent();

  async function handlePartnershipsContactForm({
    formData,
    templateForm,
  }: {
    templateForm: TemplateForm;
    formData: any;
  }) {
    const properties = {
      ...transformFormDataToEventProperties(formData),
      work_email: formData.email, // Note: this is necessary for the event to be sent to Marketo correctly as this work_email is used as a primary key.
    };
    const label = templateForm.submitButton
      ? templateForm.submitButton.buttonLabel
      : templateForm.submitButtonText ?? '';

    await sendSegmentLeadFormEvents({
      identifyTraits: {
        ...properties,
        // Note: the label here is necessary for the event to be sent to Marketo.
        // See: https://app.segment.com/udacity/destinations/marketo-v2/sources/frontend-endor/instances/5f5017123c6d5f1f65e281ce/filters
        label: 'BD Interested - Enterprise Lead - HQ',
      },
      trackAnalyticsEvent: {
        name: 'Enterprise Lead',
        type: 'submit',
        label,
        action: 'form_submitted',
        additionalAttributes: properties,
      },
      integrations: {
        sendToSalesforce: true,
      },
    });

    router.push(`/get-started/enterprise-success`);
  }

  return (
    <>
      <PageHead page={page} pageCategory={PageCategory.GetStarted} />
      <PageLayout isSimpleMode>
        <WhiteCardForm
          isSubmitting={isSubmitting}
          whiteCardFormTemplate={whiteCardFormTemplate}
          onFormSubmit={handlePartnershipsContactForm}
        />
      </PageLayout>
    </>
  );
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  // Stop additional locale pages except the default locale from being built
  if (locale !== DEFAULT_LANGUAGE) {
    return {
      notFound: true,
    };
  }

  const [page, whiteCardFormTemplate] = await Promise.all([
    getPage('/get-started/partnerships'),
    getPageTemplate<WhiteCardFormTemplate>({
      templateName: 'Partnerships Contact',
      templateQuery: whiteCardFormTemplateQuery,
      templateZodSchema: whiteCardFormTemplatesSchema,
    }),
  ]);

  return {
    props: {
      page,
      whiteCardFormTemplate,
    },
  };
}
